import type { EmAuditEntry, EmReplyState, EmThread, EmTopicKey } from '@elva/ui';
import type {
  EmailAttachment,
  EmailMessage,
  EmailMessageDetail,
  EmailReadReceipt,
  LegacyEmailThreadMessage,
  LegacyEmailThreadRow,
} from '../api/types';

type EmPatient = EmThread['patient'];
type EmMessage = EmThread['messages'][number];

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0]![0] ?? ''}${parts[1]![0] ?? ''}`.toUpperCase();
  return (parts[0]?.slice(0, 2) ?? '??').toUpperCase();
}

function formatWhen(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return iso;
  }
}

/** Normalize a backend language value to a code; '', 'en', 'english' → 'en'. */
function normLang(raw?: string | null): string {
  const v = (raw ?? '').trim().toLowerCase();
  if (!v || v === 'en' || v === 'eng' || v === 'english') return 'en';
  return v;
}

export function isNonEnglishLang(raw?: string | null): boolean {
  return normLang(raw) !== 'en';
}

export function langDisplayLabel(raw?: string | null): string {
  const code = normLang(raw);
  if (code === 'en') return 'English';
  if (code === 'es') return 'Spanish';
  if (code === 'fr') return 'French';
  return code.toUpperCase();
}

const EM_REPLY_STATES: readonly EmReplyState[] = ['needs_reply', 'drafted', 'responded', 'no_reply_needed'];

function mapReplyState(state?: string | null): EmReplyState | undefined {
  if (!state) return undefined;
  if ((EM_REPLY_STATES as readonly string[]).includes(state)) return state as EmReplyState;
  return undefined;
}

function readAiState(wire: { ai_state?: string; aiState?: string }): string {
  const raw = (wire.ai_state ?? wire.aiState ?? '').trim();
  return raw || 'none';
}

function readInboxStatus(wire: { inbox_status?: string | null }): string | undefined {
  const raw = wire.inbox_status?.trim();
  return raw || undefined;
}

function readAttachmentId(row: EmailAttachment): string | undefined {
  const wire = row as EmailAttachment & Record<string, unknown>;
  for (const key of ['attachment_id', 'id', 'attachmentId', 'handle', 'file_id', 'uuid', 'gmail_attachment_id']) {
    const raw = wire[key];
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
    if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  }
  return undefined;
}

function readAttachmentPreviewUrl(row: EmailAttachment): string | undefined {
  const wire = row as EmailAttachment & Record<string, unknown>;
  for (const key of ['preview_url', 'previewUrl', 'thumbnail_url', 'thumbnailUrl', 'url']) {
    const raw = wire[key];
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
  }
  return undefined;
}

function adaptAttachment(a: EmailAttachment, messageId?: string): EmMessage['attachments'] extends (infer U)[] | undefined ? U : never {
  const id = readAttachmentId(a);
  const name = a.name ?? a.filename ?? 'attachment';
  const contentType = (a.content_type ?? a.type ?? '').trim() || undefined;
  const size = formatBytes(a.size);
  const previewUrl = readAttachmentPreviewUrl(a);
  return {
    name,
    ...(id ? { id } : {}),
    ...(size ? { size } : {}),
    ...(contentType ? { contentType } : {}),
    ...(previewUrl ? { previewUrl } : {}),
    ...(messageId ? { messageId } : {}),
  };
}

function adaptAttachments(wire?: EmailAttachment[] | null, messageId?: string) {
  if (!wire?.length) return [];
  return wire.map((row) => adaptAttachment(row, messageId));
}

function adaptSeenBy(
  seenByWire?: EmailReadReceipt[] | null,
  seenCountWire?: number | null,
): Pick<EmMessage, 'seenBy' | 'seenCount'> {
  const seenBy = (seenByWire ?? []).map((row) => ({
    userId: row.user_id,
    name: row.name,
    readAt: row.read_at,
  }));
  const seenCount =
    typeof seenCountWire === 'number' ? seenCountWire : seenBy.length > 0 ? seenBy.length : undefined;

  if (!seenBy.length && seenCount == null) return {};
  return {
    ...(seenBy.length ? { seenBy } : {}),
    ...(seenCount != null ? { seenCount } : {}),
  };
}

/** Thread-level FD125 receipts — inherit onto inbound rows that lack per-message seen_by. */
function applyThreadSeenByToMessages(
  messages: EmMessage[],
  threadSeen: Pick<EmMessage, 'seenBy' | 'seenCount'>,
): EmMessage[] {
  const hasThreadSeen = Boolean(threadSeen.seenBy?.length || threadSeen.seenCount != null);
  if (!hasThreadSeen) return messages;

  return messages.map((message) => {
    if (message.who !== 'patient') return message;
    if (message.seenBy?.length || message.seenCount != null) return message;
    return { ...message, ...threadSeen };
  });
}

function mapMessageWho(message: LegacyEmailThreadMessage, counterpartyEmail: string): EmMessage['who'] {
  if (message.direction === 'inbound') return 'patient';
  if (message.direction === 'outbound') return message.isAI ? 'elva' : 'staff';

  const fromEmail = message.from?.email?.trim().toLowerCase() ?? '';
  const patientEmail = counterpartyEmail.trim().toLowerCase();
  if (fromEmail && patientEmail && fromEmail !== patientEmail) {
    return message.isAI ? 'elva' : 'staff';
  }

  return legacyMessageWho(message);
}

const EM_TOPIC_KEYS: readonly EmTopicKey[] = [
  'emergency',
  'booking',
  'billing',
  'insurance',
  'new_patient',
  'clinical_question',
  'records_forms',
  'complaint',
  'vendor',
  'referral',
  'marketing',
  'spam',
];

// Legacy folder / old-tag names → topic vocabulary (spec D7). ELVA is pre-launch,
// so backend will emit the new keys directly; these aliases are defensive only.
const TOPIC_ALIASES: Record<string, EmTopicKey> = {
  doctor: 'clinical_question',
  referrals: 'referral',
  vendors: 'vendor',
  new_inquiry: 'new_patient',
  // 'general' and anything unknown → no topic (fallback/none)
};

/** The one AI-assigned topic (spec D7). Accepts the backend `current_topic` string. */
function mapTopicKey(topic?: string | null): EmTopicKey | null {
  if (!topic) return null;
  if ((EM_TOPIC_KEYS as readonly string[]).includes(topic)) return topic as EmTopicKey;
  return TOPIC_ALIASES[topic] ?? null;
}

type EmTagKey = EmThread['tags'][number];

const EM_TAG_KEYS: readonly EmTagKey[] = [
  'emergency',
  'complaint',
  'booking',
  'insurance',
  'billing',
  'new_inquiry',
  'referral',
  'personal',
  'marketing',
  'spam',
];

const TOPIC_TO_TAG: Partial<Record<EmTopicKey, EmTagKey>> = {
  emergency: 'emergency',
  booking: 'booking',
  billing: 'billing',
  insurance: 'insurance',
  new_patient: 'new_inquiry',
  clinical_question: 'personal',
  records_forms: 'personal',
  complaint: 'complaint',
  vendor: 'marketing',
  referral: 'referral',
  marketing: 'marketing',
  spam: 'spam',
};

function mapTagKey(tag?: string | null): EmTagKey | null {
  if (!tag) return null;
  if ((EM_TAG_KEYS as readonly string[]).includes(tag)) return tag as EmTagKey;
  const topic = mapTopicKey(tag);
  return topic ? (TOPIC_TO_TAG[topic] ?? null) : null;
}

function adaptTags(currentTopic: EmTopicKey | null, wireTags?: string[] | null): EmTagKey[] {
  const fromWire = (wireTags ?? [])
    .map((tag) => mapTagKey(tag))
    .filter((tag): tag is EmTagKey => tag != null);
  if (fromWire.length) return [...new Set(fromWire)].slice(0, 2);
  const fromTopic = currentTopic ? TOPIC_TO_TAG[currentTopic] : null;
  return fromTopic ? [fromTopic] : [];
}

/** Backend sends `from: {name, email}`; older/mock shape used flat `from_name`/`from_email`. */
function senderName(msg: { from?: { name?: string } | null; from_name?: string }): string {
  return msg.from?.name ?? msg.from_name ?? '';
}
function senderEmail(msg: { from?: { email?: string } | null; from_email?: string }): string {
  return msg.from?.email ?? msg.from_email ?? '';
}

function formatBytes(size?: number | string): string {
  if (typeof size === 'string') return size;
  if (typeof size !== 'number' || !isFinite(size)) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function defaultPatient(name: string, email: string, lang: string): EmPatient {
  return {
    name,
    email,
    initials: initials(name),
    lang,
    status: 'active',
    balance: '$0',
    insurance: { plan: '', status: '', remaining: 0, expires: '' },
    consent: { email: true, sms: true },
  };
}

/** Normalize legacy inbox list row → canonical EmailMessage for the adapter. */
export function normalizeEmailListRow(row: LegacyEmailThreadRow | EmailMessage): EmailMessage {
  const native = row as EmailMessage;
  const legacy = row as LegacyEmailThreadRow;
  const isLegacy =
    typeof legacy.email === 'string' &&
    legacy.email.length > 0 &&
    !native.from &&
    !native.from_email &&
    !native.from_name;

  if (isLegacy) {
    const aiState = legacy.aiState ?? 'none';
    const patientName = legacy.patient?.trim() || legacy.email.split('@')[0] || '';
    return {
      id: legacy.id,
      subject: legacy.subject,
      from_name: patientName,
      from_email: legacy.email,
      email: legacy.email,
      unread: legacy.unread,
      snippet: legacy.snippet,
      current_topic: legacy.tags?.[0] ?? null,
      sender_type: legacy.patient_id != null ? 'patient' : 'unknown',
      ai_state: aiState,
      aiState,
      confidence: legacy.confidence ?? 0,
      ...(legacy.when ? { when: legacy.when, sent_at: legacy.when } : {}),
      ...(legacy.tags ? { tags: legacy.tags } : {}),
      ...(legacy.patient_id != null ? { patient_id: legacy.patient_id } : {}),
      ...(legacy.patient_id != null && legacy.patient
        ? { patient: { id: legacy.patient_id, name: legacy.patient } }
        : {}),
      ...(legacy.needsReview != null ? { needsReview: legacy.needsReview } : {}),
    };
  }

  return native;
}

/** Provider/Gmail thread id for FD125 read receipts — not used for sync. */
export function readEmailProviderThreadId(wire: unknown): string | null {
  if (!wire || typeof wire !== 'object') return null;
  const row = wire as Record<string, unknown>;
  for (const key of ['provider_thread_id', 'thread_id', 'threadId', 'gmail_thread_id']) {
    const value = row[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

/** @deprecated Use readEmailProviderThreadId — kept for older imports. */
export const readEmailSyncThreadId = readEmailProviderThreadId;

function readDetectedLang(...sources: Array<string | null | undefined>): string {
  for (const raw of sources) {
    const normalized = normLang(raw);
    if (normalized !== 'en' || (raw ?? '').trim()) return normalized;
  }
  return 'en';
}

function readMessageBodyTranslated(message: LegacyEmailThreadMessage): string | undefined {
  const wire = message as LegacyEmailThreadMessage & { body_translated?: string | null };
  const raw = message.bodyTranslated ?? message.translated_body ?? wire.body_translated ?? null;
  const trimmed = raw?.trim();
  return trimmed || undefined;
}

function legacyMessageWho(message: LegacyEmailThreadMessage): EmMessage['who'] {
  if (message.direction === 'inbound') return 'patient';
  const sender = message.sender?.toLowerCase() ?? '';
  if (sender.includes('elva')) return 'elva';
  return 'staff';
}

export function adaptEmailMessage(msg: EmailMessage): EmThread {
  const normalized = normalizeEmailListRow(msg);
  const name = senderName(normalized);
  const email = senderEmail(normalized) || normalized.email || '';
  const lang = normLang(normalized.lang);
  const patientRecord =
    normalized.patient && typeof normalized.patient === 'object' ? normalized.patient : null;
  const patientName =
    patientRecord?.name ??
    (typeof normalized.patient === 'string' ? normalized.patient : undefined) ??
    name;
  const patientId =
    patientRecord?.id != null
      ? String(patientRecord.id)
      : normalized.patient_id != null
        ? String(normalized.patient_id)
        : undefined;
  const snippet = normalized.snippet ?? normalized.digest?.summary ?? normalized.subject;
  const aiState = readAiState(normalized);
  const inboxStatus = readInboxStatus(normalized);
  const replyState = mapReplyState(normalized.reply_state);
  const currentTopic = mapTopicKey(normalized.current_topic ?? normalized.tags?.[0]);

  return {
    id: normalized.id,
    subject: normalized.subject,
    ...(normalized.subject_translated ? { subjectTr: normalized.subject_translated } : {}),
    name,
    email,
    initials: initials(name),
    lang,
    verified: normalized.sender_type === 'patient' ? 'patient' : 'unknown',
    snippet,
    when: normalized.when ?? (normalized.sent_at ? formatWhen(normalized.sent_at) : ''),
    unread: normalized.unread,
    aiState,
    ...(inboxStatus ? { inboxStatus } : {}),
    ...(replyState ? { replyState } : {}),
    confidence: normalized.confidence ?? 0,
    currentTopic,
    tags: adaptTags(currentTopic, normalized.tags),
    suggested: [],
    patient: patientId
      ? { ...defaultPatient(patientName, email, lang), patientId }
      : defaultPatient(patientName, email, lang),
    summary: snippet ?? '',
    messages: [
      {
        id: `${normalized.id}-preview`,
        who: normalized.sender_type === 'patient' ? 'patient' : 'staff',
        from: name,
        fromEmail: email,
        to: '',
        time: normalized.when ?? (normalized.sent_at ? formatWhen(normalized.sent_at) : ''),
        body: snippet,
        ...(lang !== 'en' ? { lang } : {}),
      },
    ],
    audit: [],
  };
}

function adaptActions(actions?: EmailMessageDetail['actions']): EmAuditEntry[] {
  if (!actions?.length) return [];
  return actions.map((a) => ({
    actor: a.actor,
    action: a.reason ? `${a.action} — ${a.reason}` : a.action,
    time: formatWhen(a.at),
    ai: a.actor === 'ai_receptionist',
  }));
}

export function adaptEmailMessageDetail(detail: EmailMessageDetail, base: EmThread): EmThread {
  const wireDetail = detail as EmailMessageDetail & { detected_lang?: string | null };
  const detectedLang = readDetectedLang(
    wireDetail.detectedLang,
    wireDetail.detected_lang,
    detail.language,
    detail.lang,
  );
  const baseLang = detectedLang !== 'en' ? detectedLang : base.lang;
  const threadTranslated =
    Boolean(detail.translated) ||
    Boolean(detail.messages?.some((message) => readMessageBodyTranslated(message)));
  const senderAddr = senderEmail(detail) || base.email;

  const attachments = adaptAttachments(detail.attachments ?? detail.attachment_meta, detail.id);

  let messages: EmMessage[] = [];

  if (detail.messages?.length) {
    messages = detail.messages.map((m) => {
      const who = mapMessageWho(m, senderAddr);
      const fromLabel =
        who === 'patient'
          ? base.name
          : m.sender?.trim() || m.from?.name?.trim() || m.from?.email?.trim() || (who === 'elva' ? 'Elva' : 'Staff');
      const createdAt = m.created_at ?? m.time ?? base.when;
      const wireMessage = m as LegacyEmailThreadMessage & { detected_lang?: string | null };
      const bodyTr = readMessageBodyTranslated(m);
      const msgTranslated = m.translated === true || Boolean(bodyTr);
      const msgLang = readDetectedLang(
        wireMessage.detectedLang,
        wireMessage.detected_lang,
        m.language,
        detectedLang !== 'en' ? detectedLang : undefined,
        baseLang,
      );
      const messageAttachments = adaptAttachments(m.attachments, m.id);
      return {
        id: m.id,
        who,
        from: fromLabel,
        fromEmail: who === 'patient' ? senderAddr : (m.from?.email ?? ''),
        to: who === 'patient' ? '' : senderAddr,
        time: createdAt.includes('T') ? formatWhen(createdAt) : createdAt,
        body: m.body,
        ...(bodyTr ? { bodyTr } : {}),
        ...(msgTranslated ? { translated: true } : {}),
        ...(msgLang !== 'en' ? { lang: msgLang } : {}),
        ...adaptSeenBy(m.seen_by, m.seen_count),
        ...(messageAttachments.length ? { attachments: messageAttachments } : {}),
      };
    });
  } else {
    messages = (detail.thread ?? []).map((t, i) => ({
      id: `${detail.id}-m${i}`,
      who: t.from.includes(senderAddr) ? ('patient' as const) : ('staff' as const),
      from: t.from,
      fromEmail: t.from,
      to: senderAddr,
      time: formatWhen(t.sent_at),
      body: t.snippet,
      ...(t.snippet_translated ? { bodyTr: t.snippet_translated } : {}),
      ...(baseLang !== 'en' ? { lang: baseLang } : {}),
    }));
  }

  const body = detail.body ?? detail.body_full ?? '';
  const detailSeen = adaptSeenBy(detail.seen_by, detail.seen_count);
  if (body && messages.length === 0) {
    messages = [
      {
        id: `${detail.id}-body`,
        who: 'patient',
        from: base.name,
        fromEmail: senderAddr,
        to: '',
        time: base.when,
        body,
        ...(threadTranslated &&
        detail.body_translated &&
        detail.body_translated.trim() &&
        detail.body_translated !== body
          ? { bodyTr: detail.body_translated.trim(), translated: true }
          : {}),
        ...(baseLang !== 'en' ? { lang: baseLang } : {}),
        ...(attachments.length ? { attachments } : {}),
        ...detailSeen,
      },
    ];
  } else if (attachments.length && messages.length) {
    const lastWithAttachments = [...messages].reverse().find((row) => row.attachments?.length);
    const target = lastWithAttachments ?? messages[messages.length - 1]!;
    const idx = messages.indexOf(target);
    messages[idx] = {
      ...target,
      attachments: [...(target.attachments ?? []), ...attachments],
    };
  }

  if (detail.translation?.body_translated?.trim()) {
    const anchorId = detail.translation.message_id;
    const anchorBodyTr = detail.translation.body_translated.trim();
    const anchorLang = readDetectedLang(detail.translation.detected_lang, detectedLang);
    messages = messages.map((message) => {
      if (message.id !== anchorId) return message;
      return {
        ...message,
        bodyTr: anchorBodyTr,
        translated: true,
        ...(anchorLang !== 'en' ? { lang: anchorLang } : {}),
      };
    });
  }

  messages = applyThreadSeenByToMessages(messages, detailSeen);

  const audit = adaptActions(detail.actions);
  const detailAiState = readAiState(detail);
  const aiState = detailAiState !== 'none' ? detailAiState : base.aiState;
  const inboxStatus = readInboxStatus(detail) ?? base.inboxStatus;
  const replyState = mapReplyState(detail.reply_state) ?? base.replyState;
  const autoSent = aiState === 'sent';
  const draftBodyRaw =
    detail.draft_body ??
    detail.draftBody ??
    detail.ai_draft?.text ??
    detail.ai_draft_response ??
    null;
  const draftBody = draftBodyRaw?.trim() ? draftBodyRaw.trim() : null;
  const draftConfidence = detail.confidence ?? detail.ai_draft?.confidence ?? base.confidence ?? 0;

  return {
    ...base,
    lang: baseLang,
    ...(threadTranslated ? { translated: true } : {}),
    ...(detectedLang !== 'en' ? { detectedLang } : {}),
    aiState,
    ...(inboxStatus ? { inboxStatus } : {}),
    ...(replyState ? { replyState } : {}),
    ...detailSeen,
    messages,
    audit: audit.length ? audit : base.audit,
    ...(draftBody
      ? {
          draft: {
            body: draftBody,
            reason: detail.held_reason ?? '',
            confidence: draftConfidence,
            ...(autoSent ? { autoSent } : {}),
          },
        }
      : {}),
  };
}
